// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { QUOTA_EXCEEDED_EVENT, type QuotaExceededPayload } from '@api/quotaExceeded';
import { useI18n } from '@hooks/useI18n';
import { useCommerceStore } from '@stores/commerceStore';
import { useAuthStore } from '@stores/authStore';

type MessageResolver = {
  messageKey: string;
  fallbackKey?: string;
  /** C off（商业面关闭）时的非诱导措辞（POLICY-2）。 */
  noCommerceKey?: string;
};

const MESSAGE_KEYS: Record<string, MessageResolver> = {
  daily_messages: {
    messageKey: 'quota.dailyMessages',
    fallbackKey: 'quota.dailyMessagesFallback',
    noCommerceKey: 'quota.dailyMessagesNoCommerce',
  },
  model_access: {
    messageKey: 'quota.modelAccess',
    fallbackKey: 'quota.modelAccessFallback',
    noCommerceKey: 'quota.modelAccessNoCommerce',
  },
  file_size: {
    messageKey: 'quota.fileSize',
    fallbackKey: 'quota.fileSizeFallback',
    noCommerceKey: 'quota.fileSizeNoCommerce',
  },
  kb_count: {
    messageKey: 'quota.kbCount',
    fallbackKey: 'quota.kbCountFallback',
    noCommerceKey: 'quota.kbCountNoCommerce',
  },
  context_length: {
    messageKey: 'quota.contextLength',
    fallbackKey: 'quota.contextLengthFallback',
    noCommerceKey: 'quota.contextLengthNoCommerce',
  },
  feature: {
    messageKey: 'quota.feature',
    noCommerceKey: 'quota.featureNoCommerce',
  },
};

function hasAllParams(quotaType: string, params: Record<string, string | number>): boolean {
  if (quotaType === 'daily_messages') return params.limit !== undefined;
  if (quotaType === 'model_access') return params.model !== undefined;
  if (quotaType === 'file_size' || quotaType === 'kb_count') return params.max !== undefined;
  if (quotaType === 'context_length') return params.max !== undefined;
  return true;
}

export function QuotaExceededToastBridge() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const authBootstrapDone = useAuthStore((state) => state.authBootstrapDone);
  const commerceSettings = useCommerceStore((state) => state.settings);
  const fetchCommerceSettings = useCommerceStore((state) => state.fetchCommerceSettings);
  const commerceEnabled = commerceSettings?.enabled ?? false;

  useEffect(() => {
    if (!authBootstrapDone || !isAuthenticated) {
      return;
    }
    void fetchCommerceSettings();
  }, [authBootstrapDone, fetchCommerceSettings, isAuthenticated]);

  useEffect(() => {
    const onQuotaExceeded = (event: Event) => {
      const customEvent = event as CustomEvent<QuotaExceededPayload>;
      const payload = customEvent.detail;
      if (!payload) return;

      const resolver = MESSAGE_KEYS[payload.quotaType];
      // POLICY-2：C off 时正文也不诱导升级（按钮隐藏 + 措辞替换双管齐下）。
      const primaryKey = !commerceEnabled && resolver?.noCommerceKey
        ? resolver.noCommerceKey
        : resolver?.messageKey;
      const message = resolver
        ? hasAllParams(payload.quotaType, payload.params)
          ? t(primaryKey, payload.message, payload.params)
          : resolver.fallbackKey
            ? t(resolver.fallbackKey, payload.message)
            : t(primaryKey, payload.message, payload.params)
        : payload.message
          ? payload.message
          : t(
              commerceEnabled ? 'quota.genericExceeded' : 'quota.genericExceededNoCommerce',
            );

      const options = commerceEnabled
        ? {
            action: {
              label: t('subscription.usage.upgrade'),
              onClick: () => navigate('/settings?tab=subscription'),
            },
            duration: 8000,
          }
        : { duration: 8000 };

      toast.error(message, options);
    };

    window.addEventListener(QUOTA_EXCEEDED_EVENT, onQuotaExceeded as EventListener);
    return () => window.removeEventListener(QUOTA_EXCEEDED_EVENT, onQuotaExceeded as EventListener);
  }, [commerceEnabled, navigate, t]);

  return null;
}
